import React, {useCallback, useContext, useState} from 'react';

import {MessageContext} from 'src/contexts/message-context';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';
import {useShown} from '@pb/kit-hooks-use-shown';


import {usePasswordResetEmail} from './usePasswordResetEmail';

type Props = {
    userId: number,
    disabled: boolean,
};

const MODAL_CAPTION = 'Send Password Reset';
const MODAL_WIDTH = 450;

export const SendResetPasswordEmail = ({userId, disabled}: Props) => {
    const {setSuccessMessage} = useContext(MessageContext);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const {shown, onClick, onClose} = useShown();
    const {loginActionSubmit} = useEventTracking();

    const {
        busy: isPasswordResetEmailSending,
        event: sendPasswordResetEmail,
    } = usePasswordResetEmail({
        userId,
        onSuccess: () => {
            setSuccessMessage('Password Reset email is successfully sent');
            loginActionSubmit({submit: true, success: true, itemAction: MODAL_CAPTION});
            onClose();
        },
        onFail: (error: string) => {
            setErrorMessage(error);
            loginActionSubmit({submit: true, success: false, itemAction: MODAL_CAPTION});
        },
    });

    const closeConfirmDialog = useCallback(() => {
        onClose();
        loginActionSubmit({submit: false, success: true, itemAction: MODAL_CAPTION});
    }, [onClose, loginActionSubmit]);

    return (
        <div>
            <Button disabled={disabled} onClick={onClick} variant="Type.PRIMARY">
                Send Password Reset
            </Button>
            <Dialog
                shown={shown}
                setShown={closeConfirmDialog}>
                <DialogHeader onClose={closeConfirmDialog}>
                    {MODAL_CAPTION}
                </DialogHeader>
                <DialogMain style={{padding: 24, maxWidth: MODAL_WIDTH}}>
                    {errorMessage ? <Message color="error">{errorMessage}</Message> : null}
                    <p>
                        A Password Reset Email will be sent to this user. When the user
                        clicks on the link in this email, they will be taken to the Password
                        Reset page where they will be allowed to change their password.
                    </p>
                    <p>Do you want to send Password Reset Email?</p>
                </DialogMain>
                <DialogFooter>
                    <Group>
                        <Button
                            variant="Type.PRIMARY"
                            busy={isPasswordResetEmailSending}
                            onClick={() => sendPasswordResetEmail()}>
                            Send
                        </Button>
                        <Button variant="Type.SECONDARY" onClick={closeConfirmDialog}>
                            Cancel
                        </Button>
                    </Group>
                </DialogFooter>
            </Dialog>
        </div>
    );
};
